import { camelCase } from 'lodash';

import { assertNever, StepDefinitionId } from '@willow/types-iso';

import { Expression, ExpressionEvaluator as E } from '../Expressions/ExpressionEvaluator';
import { WorkflowDefinition } from './WorkflowDefinition';

export function workflowDefinitionToMermaid({ steps }: Pick<WorkflowDefinition, 'steps'>): string {
  const nodes: { id: string; label: string }[] = steps.map((step) => ({ id: step.id, label: step.title }));

  nodes.unshift({ id: 'start', label: 'start' });

  let hasLoanStateDependency = false;

  const allStepIds = new Set(steps.map((s) => s.id));

  const edges = steps.flatMap((step) => {
    const result: string[] = [];

    if (step.automaticallyActivate) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.automaticallyActivate, allStepIds)) {
        pushEdge(edge, 'autoActivate');
      }
    }
    if (step.automaticallyComplete) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.automaticallyComplete, allStepIds)) {
        pushEdge(edge, 'autoComplete');
      }
    }
    if (step.automaticallyFail) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.automaticallyFail, allStepIds)) {
        pushEdge(edge, 'autoFail');
      }
    }
    if (step.automaticallySkip) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.automaticallySkip, allStepIds)) {
        pushEdge(edge, 'autoSkip');
      }
    }
    if (step.canManuallySkip) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.canManuallySkip, allStepIds, true)) {
        pushEdge(edge, 'canManuallySkip');
      }
    }
    if (step.canManuallyComplete) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.canManuallyComplete, allStepIds, true)) {
        pushEdge(edge, 'canManuallyComplete');
      }
    }
    if (step.canManuallyFail) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.canManuallyFail, allStepIds, true)) {
        pushEdge(edge, 'canManuallyFail');
      }
    }
    if (step.canManuallyActivate) {
      for (const edge of getEdgeDescriptionFromExpression(step.id, step.canManuallyActivate, allStepIds, true)) {
        pushEdge(edge, 'canManuallyActivate');
      }
    }

    return result;

    function pushEdge(
      edge:
        | { hasDependency: false; description?: string }
        | { hasDependency: true; dependsOn: StepDefinitionId | 'start' | 'self' | 'loanState'; description: string },
      action: string,
    ) {
      if (edge.hasDependency) {
        if (edge.dependsOn === 'loanState') {
          hasLoanStateDependency = true;
        }
        if (edge.dependsOn === 'self' || edge.dependsOn === step.id) {
          const node = nodes.find((n) => n.id === step.id);
          if (node) {
            node.label = `${node.label}<br><small>${action} ${edge.description}</small>`;
          }
        } else {
          result.push(
            `${camelCase(edge.dependsOn)} --${JSON.stringify(
              `${action}<br><small>${edge.description}</small>`,
            )}--> ${camelCase(step.id)}`,
          );
        }
      }
    }
  });

  if (hasLoanStateDependency) {
    nodes.unshift({ id: 'loanState', label: 'Loan State' });
  }

  return `
graph TD
  %% nodes
${nodes.map((s) => `  ${camelCase(s.id)}[${JSON.stringify(s.label)}]`).join('\n')}

  %% edges
${edges.map((s) => `  ${s}`).join('\n')}
  `;
}
function getEdgeDescriptionFromExpression(
  selfId: StepDefinitionId,
  expression: Expression<'boolean'> | undefined,
  allStepIds: Set<StepDefinitionId>,
  isManual?: boolean,
): (
  | {
      hasDependency: false;
      description?: string;
    }
  | {
      hasDependency: true;
      dependsOn: StepDefinitionId | 'start' | 'self' | 'loanState';
      description: string;
    }
)[] {
  if (!expression) {
    return [{ hasDependency: false, description: 'no condition' }];
  }

  const rootNode = E.parse(expression);

  switch (rootNode.name) {
    case 'BOOLEAN_LITERAL':
      switch (rootNode.value) {
        case true:
          return isManual
            ? [{ hasDependency: true, dependsOn: 'self', description: '(on user input)' }]
            : [{ hasDependency: true, dependsOn: 'start', description: '(immediately)' }];
        case false:
          return [{ hasDependency: false }];
        default:
          return assertNever(rootNode);
      }
    case 'FUNCTION_APPLICATION':
      const formIsSubmitted = E.findExpressionNodes(
        expression,
        (node) => node.name === 'FUNCTION_APPLICATION' && node.value.name === 'formIsSubmitted',
      ).map((node): { hasDependency: true; dependsOn: StepDefinitionId; description: string } => ({
        hasDependency: true,
        dependsOn: (node.value as any).parameters[0].value,
        description: 'when form is submitted',
      }));

      const stepStatusEquals = E.findExpressionNodes(
        expression,
        (node) =>
          node.name === 'FUNCTION_APPLICATION' &&
          node.value.name === 'equals' &&
          node.value.parameters.some(
            (paramNode) => paramNode.name === 'FUNCTION_APPLICATION' && paramNode.value.name === 'stepStatus',
          ),
      ).map((node): { hasDependency: true; dependsOn: StepDefinitionId; description: string } => {
        const statusNodeIndex = (node.value as any).parameters.findIndex(
          (paramNode: any) => paramNode.name === 'FUNCTION_APPLICATION' && paramNode.value.name === 'stepStatus',
        );
        const otherIndex = statusNodeIndex === 0 ? 1 : 0;
        return {
          hasDependency: true,
          dependsOn: (node.value as any).parameters[statusNodeIndex].value.parameters[0].value,
          description: `when status = ${(node.value as any).parameters[otherIndex].value}`,
        };
      });

      const allStepDefinitionIdsAccountedFor = new Set([
        ...formIsSubmitted.map((node) => node.dependsOn),
        ...stepStatusEquals.map((node) => node.dependsOn),
      ]);

      const allStringsThatAreStepDefinitionIds = new Set(
        E.findExpressionNodes(
          expression,
          (node) => node.name === 'STRING_LITERAL' && allStepIds.has(node.value as StepDefinitionId),
        ).map((node) => node.value as StepDefinitionId),
      );

      const allStepDefinitionIdsNotAccountedFor = [
        ...allStringsThatAreStepDefinitionIds.difference(allStepDefinitionIdsAccountedFor),
      ].map((definitionId) => ({
        hasDependency: true,
        dependsOn: definitionId,
        description: `unknown relationship: ${definitionId}`,
      }));

      return [...formIsSubmitted, ...stepStatusEquals, ...allStepDefinitionIdsNotAccountedFor];
    case 'NUMBER_LITERAL':
    case 'STRING_LITERAL':
      throw new Error('Root node should never be a boolean or number literal');
    default:
      return assertNever(rootNode);
  }
}
